import React from 'react';
import { Tabs, Box, SpaceBetween, Badge, Container, Header } from '@cloudscape-design/components';
import EmporiumStore from './storefronts/EmporiumStore';
import ContinentalSite from './storefronts/ContinentalSite';
import MarqueeSite from './storefronts/MarqueeSite';
import VerticalStore from './storefronts/VerticalStore';

/**
 * Seller storefront tabs (right widget) — browsable storefronts a human navigates,
 * mirroring the original demo. Emporium (shop), Continental (news), Marquee (movies/
 * TV). Browsing is FREE (humans browse free, like the WAF "human = free" model); the
 * agent's paid activity stays in the chat / activity log / Discovery tab.
 *
 * A leading Discovery tab shows what the agent discovered via /discovery/resources.
 */
const SELLERS = [
  { id: 'shop', name: 'The Emporium', kind: 'Retail', Comp: EmporiumStore },
  { id: 'publisher', name: 'The Continental', kind: 'News', Comp: ContinentalSite },
  { id: 'marquee', name: 'The Marquee', kind: 'Movies & TV', Comp: MarqueeSite },
];
const STOREFRONT_IDS = new Set(SELLERS.map((s) => s.id));

// Industry label per vertical seller (flowId from discovery → tab/heading label).
const INDUSTRY = {
  tickstream: 'Financial', formularyone: 'Healthcare', shelfsense: 'Retail Ops',
  reelrights: 'Media', gridpulse: 'Energy', inferhub: 'ISV / ML',
  datadrop: 'Data', conciergeio: 'Hospitality',
};

function StorePane({ seller, reveals, emporium, onEmporiumChange }) {
  const Comp = seller.Comp;
  // Only the Emporium has persisted human state (cart/orders); pass it through.
  if (seller.id === 'shop') {
    return <Comp reveals={reveals} store={emporium} onStoreChange={onEmporiumChange} />;
  }
  return <Comp reveals={reveals} />;
}

// Skill prices come over the wire as micro-USDC (1000 = 0.001 USDC); humanize.
function fmtAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 'priced';
  // Heuristic: values >= 1000 are micro-USDC; small decimals are already USDC.
  const usdc = n >= 1 ? n / 1_000_000 : n;
  return `${usdc.toFixed(usdc < 0.001 ? 6 : 3)} USDC`;
}

function DiscoveryPane({ discovery }) {
  if (!discovery || !discovery.sellers || discovery.sellers.length === 0) {
    return (
      <Box color="text-body-secondary" textAlign="center" padding="l">
        <div style={{ fontSize: 26 }}>🔎</div>
        <Box variant="strong">No discovery yet this session</Box>
        <Box fontSize="body-s">
          Message the agent — it queries each seller's <code>/discovery/resources</code> to learn
          what skills exist (and their prices) before paying. What it finds shows up here.
        </Box>
      </Box>
    );
  }
  return (
    <SpaceBetween size="m">
      <Box fontSize="body-s" color="text-body-secondary">
        The agent discovered {discovery.sellers.length} seller(s) and the paid services each offers.
        It can call any skill below by paying its price.
      </Box>
      {discovery.sellers.map((s, i) => (
        <Container
          key={s.sellerId || i}
          header={
            <Header variant="h3" description={s.baseUrl}>
              <SpaceBetween direction="horizontal" size="xs">
                <span style={{ textTransform: 'capitalize' }}>{s.serviceName || s.sellerId}</span>
                {s.source ? <Badge>{s.source}</Badge> : null}
              </SpaceBetween>
            </Header>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', columnGap: 12, rowGap: 6, alignItems: 'center' }}>
            {(s.skills || []).map((sk, j) => (
              <React.Fragment key={j}>
                <Box fontSize="body-s"><b>{sk.name || sk.skillId}</b></Box>
                <span style={{ justifySelf: 'start' }}>{sk.style ? <Badge color="grey">{sk.style}</Badge> : null}</span>
                <span style={{ justifySelf: 'end' }}><Badge color="blue">{sk.amount ? fmtAmount(sk.amount) : 'priced'}</Badge></span>
              </React.Fragment>
            ))}
            {(!s.skills || s.skills.length === 0) && (
              <Box fontSize="body-s" color="text-body-secondary">No skills listed.</Box>
            )}
          </div>
        </Container>
      ))}
    </SpaceBetween>
  );
}

export default function SellerTabs({ discovery, reveals = {}, activeTab, onTabChange, emporium, onEmporiumChange }) {
  // Only count SERVED items in the tab badge (a blocked/lost request fetched nothing).
  const count = (id) => (reveals[id] || []).filter((r) => !String(r.outcome || '').match(/^(lost|blocked)/)).length;
  // Vertical (API-only) sellers discovered beyond the 3 storefronts.
  const verticals = ((discovery && discovery.sellers) || []).filter((s) => !STOREFRONT_IDS.has(s.sellerId));
  return (
    <Tabs
      activeTabId={activeTab}
      onChange={({ detail }) => onTabChange(detail.activeTabId)}
      tabs={[
        { id: 'discovery', label: `Discovery (${((discovery && discovery.sellers) || []).length})`, content: <DiscoveryPane discovery={discovery} /> },
        ...SELLERS.map((s) => ({
          id: s.id,
          // Tab shows the INDUSTRY (with a count badge once the agent fetched here).
          label: count(s.id) ? `${s.kind} (${count(s.id)})` : s.kind,
          content: <StorePane seller={s} reveals={reveals[s.id] || []} emporium={emporium} onEmporiumChange={onEmporiumChange} />,
        })),
        ...verticals.map((v) => ({
          id: v.sellerId,
          label: count(v.sellerId) ? `${INDUSTRY[v.sellerId] || v.sellerId} (${count(v.sellerId)})` : (INDUSTRY[v.sellerId] || v.sellerId),
          content: <VerticalStore sellerId={v.sellerId} reveals={reveals[v.sellerId] || []} />,
        })),
      ]}
    />
  );
}
